/**
 * Bulk-price upsell on basket lines (slide-out basket and basket page).
 *
 * For each line whose variant has Hulk Volume Discount tiers:
 *   "Add 24 more to save 5% (£3.13 each)"  [Add 24]
 *   "Saving 5%. Add 25 more to save 10% (£2.96 each)"  [Add 25]
 *   "Top bulk price: you're saving 20%"
 * The button sets the line's quantity through the basket's own quantity box,
 * so the normal update runs (assets/cart.js in the drawer, the form on the
 * basket page) and the app's discount does the rest.
 *
 * Tiers come from the app's own offer endpoint, the same request its table on
 * product pages makes, cached per variant for the browser session (10 min).
 * The app counts each line on its own (20 black + 5 translucent boards don't
 * reach 25+), so each line is judged by its own quantity. Only "% Off" tiers
 * are shown; anything else, or the endpoint failing, shows nothing.
 * Markup: snippets/bulk-upsell.liquid. Prices ex VAT, as in the basket.
 */
(function () {
  'use strict';

  var TTL = 10 * 60 * 1000;
  var inflight = {};
  var gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  function hulk() { return window.hulkapps || {}; }
  function api() { return (hulk().vd_url || 'https://volumediscount.hulkapps.com') + '/api/v2/shop/get_offer_table'; }
  function pct(n) { return (Math.round(n * 10) / 10) + '%'; }

  /* "each_qty" offers: [["25","5","% Off","","0"], …] → [{ min: 25, pct: 5 }, …] */
  function parse(j) {
    var o = j && j.eligible_offer;
    if (!o || o.main_offer_type !== 'volume' || o.discount_type !== 'each_qty') return [];
    var levels;
    try { levels = typeof o.offer_levels === 'string' ? JSON.parse(o.offer_levels) : o.offer_levels; } catch (e) { return []; }
    if (!Array.isArray(levels)) return [];
    var out = [];
    for (var i = 0; i < levels.length; i++) {
      var min = parseInt(levels[i][0], 10), val = parseFloat(levels[i][1]);
      if (!(min > 0) || !(val > 0) || !/%/.test(String(levels[i][2]))) return [];
      out.push({ min: min, pct: val });
    }
    return out.sort(function (a, b) { return a.min - b.min; });
  }

  function cacheKey(vid) { return 'binbase:bulk:' + vid; }
  function fromCache(vid) {
    try {
      var c = JSON.parse(sessionStorage.getItem(cacheKey(vid)));
      if (c && Date.now() - c.t < TTL) return c.tiers;
    } catch (e) { /* no storage: fetch again */ }
    return null;
  }
  function toCache(vid, tiers) {
    try { sessionStorage.setItem(cacheKey(vid), JSON.stringify({ t: Date.now(), tiers: tiers })); } catch (e) { /* fine */ }
  }

  function load(el) {
    var vid = el.getAttribute('data-vid');
    if (inflight[vid]) return inflight[vid];
    var h = hulk(), c = h.customer || null;
    var body = new URLSearchParams();
    body.append('pid', el.getAttribute('data-pid'));
    body.append('store_id', h.store_id || el.getAttribute('data-store'));
    if (c && Array.isArray(c.tags)) c.tags.forEach(function (t) { body.append('ctags[]', t); });
    else body.append('ctags', (c && c.tags) || '');
    body.append('customer_orders_count', c && c.orders_count != null ? String(c.orders_count) : '');
    body.append('product_variants', vid);
    body.append('product_collections', el.getAttribute('data-cols') || '');
    (el.getAttribute('data-tags') || '').split('|').filter(Boolean).forEach(function (t) { body.append('product_tags[]', t); });
    body.append('geo_country', h.geo_country || '');
    body.append('market_country', h.market_country || 'GB');
    inflight[vid] = fetch(api(), { method: 'POST', body: body })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (j) { var t = parse(j); toCache(vid, t); return t; })
      .catch(function () { return []; })
      .then(function (t) { delete inflight[vid]; return t; });
    return inflight[vid];
  }

  /* The quantity shown in the line's box (it changes before the basket does) */
  function lineQty(el) {
    var line = el.closest('[data-line-key], [data-page-line]');
    var input = line && line.querySelector('[data-cart-qty-in], input[name^="updates"]');
    var n = input ? parseInt(input.value, 10) : NaN;
    return isNaN(n) ? (parseInt(el.getAttribute('data-qty'), 10) || 0) : n;
  }

  function show(el, tiers) {
    var q = lineQty(el);
    var text = el.querySelector('[data-bulkup-text]');
    var btn = el.querySelector('[data-bulkup-add]');
    if (!tiers || !tiers.length || q < 1 || !text || !btn) { el.hidden = true; return; }
    var reached = null, next = null;
    tiers.forEach(function (t) { if (q >= t.min) reached = t; else if (!next) next = t; });
    var price = parseFloat(el.getAttribute('data-price')) || 0;
    if (next) {
      var add = next.min - q;
      text.innerHTML = (reached ? 'Saving ' + pct(reached.pct) + '. ' : '') + 'Add <b>' + add + ' more</b> to save ' + pct(next.pct) +
        (price > 0 ? ' (' + gbp.format(Math.round(price * (1 - next.pct / 100)) / 100) + ' each)' : '');
      btn.textContent = 'Add ' + add;
      btn.setAttribute('data-bulkup-add', String(next.min));
      btn.setAttribute('aria-label', 'Add ' + add + ' more, making ' + next.min);
      btn.hidden = false;
    } else {
      text.innerHTML = 'Top bulk price: you’re saving <b>' + pct(reached.pct) + '</b>';
      btn.hidden = true;
    }
    el.classList.toggle('is-top', !next);
    el.hidden = false;
  }

  function fill(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-bulkup]').forEach(function (el) {
      var t = fromCache(el.getAttribute('data-vid'));
      if (t) { show(el, t); return; }
      load(el).then(function (tiers) { if (document.contains(el)) show(el, tiers); });
    });
  }

  function refollow(line) {
    var el = line && line.querySelector('[data-bulkup]');
    if (!el) return;
    var t = fromCache(el.getAttribute('data-vid'));
    if (t) show(el, t);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bulkup-add]');
    if (btn) {
      var line = btn.closest('[data-line-key], [data-page-line]');
      var input = line && line.querySelector('[data-cart-qty-in], input[name^="updates"]');
      var to = parseInt(btn.getAttribute('data-bulkup-add'), 10);
      if (!input || !(to > 0)) return;
      input.value = to;
      if (line.matches('[data-page-line]')) line.classList.add('is-pending');
      input.dispatchEvent(new Event('change', { bubbles: true }));
      refollow(line);
      return;
    }
    /* +/− steppers: cart.js has already changed the number, follow it */
    var step = e.target.closest('[data-cart-qty], [data-page-qty]');
    if (step) {
      var l = step.closest('[data-line-key], [data-page-line]');
      setTimeout(function () { refollow(l); }, 0);
    }
  });
  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('[data-cart-qty-in], [data-page-line] input[name^="updates"]')) {
      refollow(e.target.closest('[data-line-key], [data-page-line]'));
    }
  });

  /* The drawer is rebuilt after every basket change (assets/cart.js announces it) */
  document.addEventListener('theme:cart:rendered', function () { fill(document.querySelector('[data-cart-drawer]')); });

  function start() {
    fill(document.querySelector('[data-cart-page]'));
    /* Warm the cache for what's already in the basket, so the drawer opens complete */
    setTimeout(function () { fill(document.querySelector('[data-cart-drawer]')); }, 2000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
